import React, { useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import styles from './Product.module.scss'
import Breadcrumbs from '../../../components/Breadcrumbs'
import FormattedMessage from '../../../components/FormattedMessage'
import MetaTags from '../../../components/MetaTags'
import Notification from '../../../components/Notification/Notification'
import PageLayout from '../../../components/PageLayout'
import PrerenderRedirect from '../../../components/PrerenderRedirect'
import ProductBasic from '../../../components/Product/BasicInfo'
import SizeGuide from '../../../components/Product/SizeGuide'
import ProductPromotion from '../../../components/ProductPromotion'
import ChannelContext from '../../../context/ChannelContext'
import useClubContext from '../../../hooks/club/clubContext'
import useMatchMedia from '../../../hooks/matchMedia'
import useProduct from '../../../hooks/product'
import useSizeGuide from '../../../hooks/sizeGuide/sizeGuide'
import { CatalogListItemInterface } from '../../../interfaces/CatalogInterface'
import * as api from '../../../services/product/product.api'
import { State } from '../../../services/reducers'
import { toCatalogProducts } from '../../../transformers/CatalogTransformer'
import { getBreadcrumbs, getMetaTags } from '../../../utils/product'

type URLParamsType = {
  productId: string
  productSlug?: string
  categorySlug?: string
}

type Props = {}

const SIZE_PANEL_ID = 'size-panel'
const MIN_REQUIRED_CROSS_PRODUCTS = 4

const Product = (props: Props) => {
  const _mounted = useRef<boolean>(false)
  const [crossProducts, setCrossProducts] = useState<CatalogListItemInterface[]>([])
  const [upSellProducts, setUpSellProducts] = useState<CatalogListItemInterface[]>([])
  const urlParams = useParams<URLParamsType>()
  const language = useSelector((state: State) => state.locale.language)
  const fallbackLanguageIsoCode = useSelector((state: State) => state.global.fallbackLanguageIsoCode)
  const channel = useContext(ChannelContext).current
  const categories = channel.categories
  const { isDesktop } = useMatchMedia()
  const { setProduct, getAvailability, product, isNoneSize } = useProduct()
  const clubContext = useClubContext()
  const sizeGuide = useSizeGuide()

  let prerenderTags = null

  useEffect(() => {
    _mounted.current = true
    setCrossProducts([])
    setUpSellProducts([])

    const fetchProduct = async () => {
      try {
        const response = await api.readProduct(parseInt(urlParams.productId, 10))
        if (_mounted.current) {
          setProduct(response.data)
        }
      } catch (error: any) {
        console.error(error)
        setProduct(false)
      } finally {
        fetchCrossProducts()
        fetchUpSellProducts()
      }
    }

    const fetchCrossProducts = async () => {
      try {
        const response = await api.readCrossProducts(parseInt(urlParams.productId, 10))
        if (_mounted.current) {
          setCrossProducts(toCatalogProducts(response.data))
        }
      } catch (error: any) {
        console.error(error)
      }
    }

    const fetchUpSellProducts = async () => {
      try {
        const response = await api.readUpSellProducts(parseInt(urlParams.productId, 10))
        if (_mounted.current) {
          setUpSellProducts(toCatalogProducts(response.data))
        }
      } catch (error: any) {
        console.error(error)
      }
    }

    fetchProduct()

    return () => {
      _mounted.current = false
    }
  }, [urlParams.productId])

  const { title, description, canonicalPath } = getMetaTags(categories, product!, language.isoCode, fallbackLanguageIsoCode, channel)
  const loading = !product || parseInt(urlParams.productId, 10) !== product?.id!
  let content = (
    <>
      <Breadcrumbs classes={{ root: styles['product__breadcrumbs'] }} />
      <ProductBasic channel={channel} product={undefined} sizePanelId={SIZE_PANEL_ID} key="skeleton" />
    </>
  )

  if (!loading && product) {
    const breadcrumbs = getBreadcrumbs(channel, product, language.isoCode, fallbackLanguageIsoCode)
    const availability = getAvailability(product)

    if (availability!?.available < 1 || breadcrumbs.length === 0) {
      const parentCategory = breadcrumbs[breadcrumbs.length - 1]
      prerenderTags = <PrerenderRedirect to={parentCategory ? parentCategory.link : '/'} />
    }

    content = (
      <>
        <Breadcrumbs classes={{ root: styles['product__breadcrumbs'] }} items={clubContext.isInClubFlow ? [] : breadcrumbs} />
        <ProductBasic
          channel={channel}
          product={product}
          sizePanelId={SIZE_PANEL_ID}
          hasValidSizePreset={Boolean(product?.sizeGuidePreset && sizeGuide.getPresetById(product.sizeGuidePreset))}
        />
        {isDesktop && product.sizeGuidePreset ? (
          <SizeGuide panelId={SIZE_PANEL_ID} presetId={product.sizeGuidePreset} classes={{ root: styles['product__size-guide'] }} />
        ) : null}
        {!clubContext.isInClubFlow ? (
          <ProductPromotion
            products={upSellProducts}
            title={
              <FormattedMessage
                id="Product.YouMayAlsoLikeUpSell"
                description="Product.YouMayAlsoLikeUpSell"
                defaultMessage="You May Also Like"
              />
            }
          />
        ) : null}
        {!clubContext.isInClubFlow && crossProducts.length >= MIN_REQUIRED_CROSS_PRODUCTS ? (
          <ProductPromotion
            products={crossProducts}
            title={
              <FormattedMessage
                id="Product.PeopleAlsoBoughtCrossSell"
                description="Product.PeopleAlsoBoughtCrossSell"
                defaultMessage="People also bought"
              />
            }
          />
        ) : null}
      </>
    )
  }

  if (product === false) {
    return (
      <PageLayout>
        <MetaTags
          title={title}
          description={description}
          canonical={canonicalPath}
          imageWidth="300px"
          imageHeight="266px"
          imageAlt="Logo"
        />
        <div className={styles['product']}>
          <Notification
            flat
            id={-1}
            type="warning"
            content={
              <FormattedMessage
                id="Product.SorryProductNotFound"
                // eslint-disable-next-line max-len
                defaultMessage="Sorry we couldn't find what you're looking for. You can try to search for it, just click search icon in menu."
              />
            }
            className={styles['product--not-found']}
          />
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <MetaTags
        title={title}
        description={description}
        canonical={canonicalPath}
        product={product}
        imageWidth="300px"
        imageHeight="266px"
        imageAlt="Logo"
        hrefLangs
      />
      {prerenderTags}
      <div className={styles['product']}>{content}</div>
    </PageLayout>
  )
}

export default Product
